import type { ListingButtonKind } from '@pulsify/shared/dto'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { ChevronLeft, Heart, Store } from 'lucide-react'
import * as m from '~/paraglide/messages'
import { SPRING } from '~/shared/lib/motion'
import { cn } from '~/shared/lib/utils'
import { BUTTON_ICONS, BUTTON_LABELS } from './button-kinds'

interface PhonePreviewProps {
  name: string
  coverImageUrl: string | null
  city: string
  buttons: ListingButtonKind[]
}

export function PhonePreview({ name, coverImageUrl, city, buttons }: PhonePreviewProps) {
  const reduced = useReducedMotion()

  return (
    <figure
      aria-label={m.listingButtonsPreviewLabel()}
      className="relative m-0 mx-auto w-[min(320px,100%)] rounded-[2.5rem] bg-foreground p-2 dark:bg-muted"
    >
      <div className="relative h-[440px] overflow-hidden rounded-[2rem] bg-background text-foreground">
        <div className="relative h-44 overflow-hidden">
          {coverImageUrl ? (
            <img src={coverImageUrl} alt="" className="size-full object-cover" draggable={false} />
          ) : (
            <div className="flex size-full items-center justify-center bg-muted">
              <Store className="size-10 text-muted-foreground/40" aria-hidden />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
          <div className="absolute inset-x-4 top-4 flex items-center justify-between">
            <span className="flex size-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-md">
              <ChevronLeft className="size-4" aria-hidden />
            </span>
            <span className="flex size-8 items-center justify-center rounded-full bg-background/70 backdrop-blur-md">
              <Heart className="size-4" aria-hidden />
            </span>
          </div>
        </div>

        <div className="relative -mt-4 flex flex-col gap-4 px-4">
          <div>
            <p className="line-clamp-1 font-semibold font-serif text-xl leading-tight">{name}</p>
            <p className="line-clamp-1 text-muted-foreground text-xs">{city}</p>
          </div>
          {buttons.length > 0 && (
            <div
              className="grid gap-1.5"
              style={{ gridTemplateColumns: `repeat(${buttons.length}, minmax(0, 1fr))` }}
            >
              <AnimatePresence initial={false} mode="popLayout">
                {buttons.map((kind, index) => {
                  const Icon = BUTTON_ICONS[kind]
                  return (
                    <motion.div
                      key={kind}
                      layout={!reduced}
                      initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.9 }}
                      transition={reduced ? { duration: 0.15 } : SPRING}
                      className={cn(
                        'flex h-12 min-w-0 flex-col items-center justify-center gap-0.5 rounded-xl px-1',
                        index === 0
                          ? 'bg-foreground/90 text-background'
                          : 'bg-foreground/5 text-foreground',
                      )}
                    >
                      <Icon className="size-4" aria-hidden />
                      <span className="w-full truncate text-center font-semibold text-[11px] leading-tight">
                        {BUTTON_LABELS[kind]()}
                      </span>
                    </motion.div>
                  )
                })}
              </AnimatePresence>
            </div>
          )}
          <div className="flex flex-col gap-1.5" aria-hidden>
            <span className="h-2 w-3/4 rounded-full bg-muted" />
            <span className="h-2 w-1/2 rounded-full bg-muted" />
          </div>
        </div>
      </div>
    </figure>
  )
}
